/**
 * The walkaround's camera rig: one focus point, one altitude, one yaw/pitch
 * orbit at every altitude, the plot-frame pivot and eye below the SURFACE
 * edge, the tilt looked up past the pitch floor, and the ladder's own tier
 * framing above it.
 *
 * Below 500 m the walkaround is a local frame of its own at the scene
 * origin, in real metres, so `orbit.distance` is DERIVED from the altitude,
 * never the reverse.
 */
import * as THREE from 'three';

import { altitudeForTier, metresPerPixel } from './camera.ts';
import { urlFlag, TEST_SWITCHES } from '../urlFlags.ts';
import type { BodyRow } from '../world/types';
import type { AtlasLive, AtlasState, Site, Tier } from '../atlas';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import type { DescentAnchorHandle } from './descentAnchor.ts';
import type { FarFieldHandle } from './farField.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { InjectsHandle } from './injects.ts';
import type { MountHandle } from './mount.ts';
import type { NavigationHandle } from './navigation.ts';
import type { PlotControlsHandle } from './panels/plotControls.ts';
import type { PlotBuildHandle } from './plotBuild.ts';
import type { PlotGeometryHandle } from './plotGeometry.ts';
import type { PlotMountHandle } from './plotMount.ts';
import type { RoverDriveHandle } from './roverDrive.ts';
import type { ViewClockHandle } from './viewClock.ts';
import type { WorldHostState } from './worldHost.ts';

export const CINEMA_DUR = 52;

export interface CameraRigDeps {
  readonly focusRig: FocusRigHandle;
  readonly mount: MountHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly descentAnchorHandle: DescentAnchorHandle;
  readonly plotBuild: PlotBuildHandle;
  readonly plotMount: PlotMountHandle;
  readonly plotGeometry: PlotGeometryHandle;
  readonly plotControls: PlotControlsHandle;
  readonly roverDrive: RoverDriveHandle;
  readonly bodyScene: BodySceneHandle;
  readonly viewClockHandle: ViewClockHandle;
  readonly injects: InjectsHandle;
  readonly worldHostState: WorldHostState;
  readonly live: AtlasLive;
  /** Built after this module, so atlas.ts hands these over through getters. */
  readonly farField: FarFieldHandle;
  readonly navigation: NavigationHandle;
  readonly SCENE_NEAR: number;
  readonly root: HTMLElement;
  site(): Site | undefined;
  readonly state: AtlasState;
}

export interface CameraRigHandle {
  readonly WALK_BODY_R_M: number;
  readonly _aimW: THREE.Vector3;
  applyNearClipInject(): boolean;
  applyPlotTilt(): void;
  applyTierFraming(tier: Tier, altM?: number, aim?: { lat: number; lon: number } | null): void;
  aspect(): number;
  readonly camPos: THREE.Vector3;
  readonly camTarget: THREE.Vector3;
  readonly cinemaLook: THREE.Vector3;
  readonly cinemaPos: THREE.Vector3;
  enterPlotRegime(): void;
  enterSurfaceBand(): void;
  formatAltitude(m: number): string;
  formatSample(): string;
  inLocalSky(row: BodyRow): boolean;
  ladderOwnsFrame(): boolean;
  leaveSurfaceBand(): void;
  readonly orbit: { distance: number; lookAt: THREE.Vector3 };
  orbitViewBy(dYaw: number, dPitch: number): void;
  plotCamera(): boolean;
  plotDistanceFromAltitude(): number;
  plotEye(): THREE.Vector3;
  plotEyeFloorM(): number;
  plotPitchFloor(): number;
  plotPivot(out: THREE.Vector3): THREE.Vector3;
  plotTiltMax(): number;
  plotUnitsM(): number;
  placeFocusCamera(): void;
  readCameraWorld(): void;
  setCameraNear(units: number): void;
  shipPlace(): { solM: number; nearId: string | null; nearM: number } | null;
  stopCinema(): void;
  surfaceRegime(): boolean;
  syncYawBasis(): void;
  tileWalk(forward: number, right: number, frac: number): void;
  tileWalkaround(): boolean;
  readonly targetLook: THREE.Vector3;
  readonly targetPos: THREE.Vector3;
  readonly lookUpDebug: {
    noTilt: boolean; skyHidden: boolean; skipReturnTiltReset: boolean; skipJumpTiltReset: boolean;
    sunUv: boolean; starsHidden: boolean; dayStars: boolean; whiteSun: boolean; whiteGlow: boolean;
    whiteLight: boolean; noGreenTint: boolean; unlitFar: boolean;
  };
  plotTilt: number;
  cinema: number | null;
  cinemaReleaseInject: boolean;
  handoverMarsTint: boolean;
  handoverStepM: number;
  legacyPlotHandover: boolean;
  legacyYawBasis: boolean;
  nearClipInject: boolean;
}

export function createCameraRig(deps: CameraRigDeps): CameraRigHandle {
  const { root, site, state, SCENE_NEAR } = deps;
  const { altitude, band, fitOpening, focusCam } = deps.focusRig;
  const { camera, cameraWorld } = deps.mount;
  const { descendable, rowById } = deps.bodyReaders;
  const { descentAnchor, standLatLon } = deps.descentAnchorHandle;
  const { ensurePlotMesh } = deps.plotBuild;
  const { plotUpWorld, plotQuat, plotWorld } = deps.plotMount;
  const { regionXZAt } = deps.plotGeometry;
  const { pcFly, setPlaying } = deps.plotControls;
  const { GROUNDED_RETURN_OLD } = deps.injects.state;
  const { externalCamWorld } = deps.worldHostState;

  const camPos = new THREE.Vector3(0, 0, 0);
  const camTarget = new THREE.Vector3(0, 0, 0);
  const targetPos = camPos.clone();
  const targetLook = camTarget.clone();
  /** The aim, carried out of the plot's frame into the drawn one. */
  const _aimW = new THREE.Vector3();

  // ── the flyover ───────────────────────────────────────────────────────────
  // Seconds into a cinematic pass, or null. A glide down from altitude into a
  // figure-eight over the claim, with the sun running underneath it — the point
  // is not the camera move, it is that the ground CHANGES while you cross it.
  let cinema: number | null = null;
  const cinemaPos = new THREE.Vector3();
  const cinemaLook = new THREE.Vector3();

  const orbit = {
    // Overwritten by `plotDistanceFromAltitude()` before it is ever read; a
    // metres-scale placeholder so a stray frame before that isn't a NaN or a
    // camera at the origin.
    distance: 1.2 * plotUnitsM(),
    lookAt: new THREE.Vector3(0, 0, 0),
  };
  /**
   * From below, a plot is the flat underside of a prism with a sky in it.
   *
   * 8 degrees rather than the 2.6 it was: at 2.6 the eye lies flat against the
   * ground, the horizon sits a fifth of the frame under its own top edge and
   * every metre of relief in front of the camera fills the frame.
   */
  const PLOT_PITCH_FLOOR = 0.14;
  /** The rover's chase view is this same rig, and its own pose predates both floors. */
  const ROVER_PITCH_FLOOR = 0.045;
  /**
   * How far over the drawn mesh the walkaround's eye is floored: a mast, not a boot.
   *
   * The floor is on the eye's height over the PIVOT, which is on the ground, so
   * it is `plotDistanceFromAltitude`'s floor that carries it and the aim stays
   * along the ground; lifting the eye alone would tilt the view into it.
   */
  const SURFACE_EYE_FLOOR_M = 5.0;
  /**
   * How wide the thing WASD steers is, for the boulders it is steered into.
   *
   * A pair of shoulders. The pivot is a point and a point can thread between
   * two rocks a hand's width apart, which reads as walking through both.
   */
  const WALK_BODY_R_M = 0.6;
  /** What the chase view was floored at before the walkaround's own floor rose. */
  const ROVER_EYE_FLOOR_M = 1.6;
  /** A floor on the DISTANCE rather than the eye: how close the rig may orbit at all. */
  const PLOT_MIN_DISTANCE_M = 5;
  /** The walkaround's view turned up about the eye, radians, on top of the pivot's pose; a drag
   *  carries on past the pitch floor into it. */
  let plotTilt = 0;
  /**
   * `descent-dojo --lookup`'s controls: the tilt held at zero, the sky's bodies
   * hidden again, and the two new resets skipped one at a time so `look-reset`
   * and `look-jump-reset` can show each one is what clears the tilt. `sunUv`
   * and `starsHidden` put back the 304 A map on the sun from the ground and
   * the star field retired with the orrery; `dayStars` holds the field at
   * full by day; `whiteSun` draws the sun from the ground white whatever
   * the air it is seen through, `whiteGlow` puts the sky's brightest light
   * back on the tone curve's own white, and `whiteLight` lights the ground in
   * the body's own hue at every height of the sun (`descent-dojo --sunset`),
   * `noGreenTint` the same for green alone. `unlitFar` puts the far ground
   * and haze back off the deck's own light
   * (`descent-dojo --overcast --inject unlit-far`).
   */
  const lookUpDebug = {
    noTilt: false, skyHidden: false, skipReturnTiltReset: false, skipJumpTiltReset: false,
    sunUv: false, starsHidden: false, dayStars: false, whiteSun: false, whiteGlow: false, whiteLight: false,
    noGreenTint: false, unlitFar: false,
  };

  function plotPitchFloor(): number {
    return deps.roverDrive.state.driving ? ROVER_PITCH_FLOOR : PLOT_PITCH_FLOOR;
  }
  function plotEyeFloorM(): number {
    return deps.roverDrive.state.driving ? ROVER_EYE_FLOOR_M : SURFACE_EYE_FLOOR_M;
  }

  /** The claim's own extent, metres, or a stand-in before it lands. */
  function plotUnitsM(): number {
    return deps.live.plot ? deps.live.plot.clusterExtentM : 14000;
  }
  /**
   * The SURFACE band: the altitude at which the walkaround is the view.
   *
   * CLAIM and PLOT used to live here too, on a rig in plot units at the scene
   * origin while a tile was metres about Mars's centre, which is why the tile
   * group had to be hidden below 20 km. The leaves are the terrain at both
   * bands now, so the handover retreats to the walkaround
   * (02-continuous-descent.md, "The camera").
   */
  function surfaceRegime(): boolean {
    return band() === 'SURFACE';
  }
  /** `surface-clamp --frames --inject plot-handover`: the band edge alone again. */
  let legacyPlotHandover = false;
  /** `surface-clamp --handover`'s two controls, both off unless an arm sets them. */
  let handoverMarsTint = false;
  let handoverStepM = 0;
  /**
   * Whether the walkaround's own local frame owns the camera and the frame.
   *
   * The band AND a mesh to stand on. The band alone used to be the whole
   * question, and a body with no `sites.json` row never builds a mesh: at 490 m
   * over Mercury the eye left the world frame for a rig measured in a 14 km
   * stand-in extent, aimed at that rig's own origin rather than at the ground -
   * 39 degrees ABOVE the local horizontal, having passed through tangential to
   * get there - while the same edge retired the orrery and the pyramid, which
   * were the only things drawn. The frame went black and stayed there.
   *
   * False while cruise draws: the frame is the sim camera's then, and the air
   * posed in this y-up basis instead of the body's own radial drew a second
   * shell thousands of kilometres off the globe under the ship.
   */
  function plotCamera(): boolean {
    return ladderOwnsFrame() && surfaceRegime() && (legacyPlotHandover || deps.live.plot !== null);
  }
  /**
   * Is the frame about to draw the Atlas's own?
   *
   * The walkaround's basis - the sky's up, the far field's matrix on the world
   * root - is measured from a camera this mount poses itself. Cruise poses its
   * own and draws through the same subtree, so out there that basis places the
   * body about a centre the drawn camera is not looking from.
   */
  function ladderOwnsFrame(): boolean {
    return !deps.worldHostState.externalWorld || GROUNDED_RETURN_OLD;
  }
  /**
   * The band table's own ruler: what one rendered pixel covers on the ground.
   *
   * Measured against the STAGE's height rather than the window's, because the
   * pane is what the Atlas draws into (02-continuous-descent.md, "Altitude
   * bands" derives every edge from this number).
   */
  function formatSample(): string {
    const h = Math.max(1, root.clientHeight);
    const mpp = metresPerPixel(altitude(), camera.fov, h);
    if (mpp < 1000) return `${mpp.toFixed(mpp < 10 ? 2 : 0)} m/px`;
    return `${(mpp / 1000).toFixed(0)} km/px`;
  }

  /** Where the ship is, for the card its own marker raises. Null before a position. */
  function shipPlace(): { solM: number; nearId: string | null; nearM: number } | null {
    if (deps.bodyScene.state.shipMarker === null || !deps.bodyScene.state.shipMarker.known) return null;
    const w = deps.bodyScene.state.shipMarker.world;
    let nearId: string | null = null;
    let nearM = Infinity;
    for (const b of deps.bodyScene.state.orrery?.bodies ?? []) {
      const d = Math.hypot(b.world.x - w.x, b.world.y - w.y, b.world.z - w.z);
      if (d < nearM) { nearM = d; nearId = b.row.id; }
    }
    return { solM: Math.hypot(w.x, w.y, w.z), nearId, nearM };
  }

  /** An altitude a player can read: metres close in, kilometres out, AU beyond. */
  function formatAltitude(m: number): string {
    if (m < 1000) return `${m.toFixed(0)} m`;
    if (m < 1e9) return `${(m / 1000).toFixed(m < 1e6 ? 1 : 0)} km`;
    return `${(m / 1.495978707e11).toFixed(2)} AU`;
  }

  function aspect(): number {
    const w = Math.max(1, root.clientWidth);
    const h = Math.max(1, root.clientHeight);
    return w / h;
  }

  /**
   * Point the camera at where the ladder rung wants to look.
   *
   * The five hand-tuned per-tier rigs collapse to this: the focus is the
   * surveyed site's own ground once a body is picked, and the rung only says how
   * high and how obliquely. The heliocentric view is the one that focuses a
   * centre, because a body 47 AU away has no ground to look at yet.
   */
  function applyTierFraming(
    tier: Tier,
    altM?: number,
    aim?: { lat: number; lon: number } | null,
  ): void {
    if (tier === 0 || state.bodyId === null) {
      fitOpening(aspect());
      deps.live.cameraDirty = true;
      return;
    }
    const row = rowById(state.bodyId);
    // Nothing to frame on a body with no row: the registry is a round trip
    // away and its load handler reframes once it lands.
    if (!row) return;
    // A caller that names the ground it wants framed overrides the descent's
    // own anchor, which is the CLAIM's centre: ENTER PLOT is pressed on one
    // cell of it, and at Jezero the two are up to a kilometre apart.
    const a = aim ?? descentAnchor();
    // The rung's own framing unless the caller brought an altitude: PLOT and
    // SURFACE share tier 6, so a reframe that always took the rung's 1,500 m
    // bounced every ask below the 500 m band edge back up to PLOT.
    const alt = altM ?? altitudeForTier(tier, row.radius_m);
    // The hold is what lets a caller keep the centre anchor on a body that has
    // a site: without it this branch re-attaches the site on every reframe.
    if (a && descendable() && !deps.bodyScene.state.centreAnchorHold) {
      focusCam.goTo(state.bodyId, a.lat, a.lon, alt);
      // Oblique at the top of the ladder so the limb reads, closing toward
      // overhead as the ground becomes the subject.
      focusCam.setOrientation(0, tier <= 1 ? 0.35 : tier === 2 ? 0.75 : 1.05);
    } else {
      focusCam.goTo(state.bodyId, null, null, alt);
    }
    if (surfaceRegime()) {
      enterPlotRegime();
      enterStandingPose();
    }
    deps.live.cameraDirty = true;
    // The rung asked for an altitude and a class (b) or (c) floor may have
    // clamped it: the chip, the breadcrumb and the hex layer follow the
    // altitude the camera took, not the one the rung wanted. Nothing else
    // re-synced it, so `?body=europa&atlasTier=4` read SURVEY with an empty
    // hex ring at a REGIONAL altitude until the first wheel notch.
    deps.navigation.syncTierToAltitude();
  }

  /**
   * The walkaround's pivot in the plot's own metres: `standLatLon` on the mesh.
   *
   * NOT the scene origin, which is the hex cluster's centroid
   * (`this.group.position` in src/atlas/dtmPlot.ts recentres on it): the ladder
   * has aimed at the anchor the whole way down and the cluster's centroid is
   * 969 m from it at Hadley and 697 m at Jezero, so pivoting on the origin
   * moved the ground under the eye by that much in the one notch that crosses
   * the SURFACE edge. Before the mesh lands there is nothing to place it on and
   * the origin is all there is.
   */
  function plotPivot(out: THREE.Vector3): THREE.Vector3 {
    out.set(0, 0, 0);
    const a = standLatLon();
    const xz = a ? regionXZAt(a.lat, a.lon) : null;
    if (!deps.live.plot || !xz) return out;
    deps.live.plot.group.updateMatrix();
    return out.set(xz.x, 0, xz.z).applyMatrix4(deps.live.plot.group.matrix);
  }

  // The plot rig's yaw 0 is the region's north and the focus camera's is its carried pole frame, at any azimuth
  // to that north as the body turns, so the one shared yaw is re-measured whenever the rig reading it changes.
  let yawOnPlot = false;
  /** `descent-dojo --band-pop --inject yaw-basis`: the yaw handed across unconverted. */
  let legacyYawBasis = false;
  const _yawN = new THREE.Vector3();
  const _yawE = new THREE.Vector3();
  const _yawD = new THREE.Vector3();
  function syncYawBasis(): void {
    const onPlot = plotCamera();
    if (onPlot === yawOnPlot) return;
    if (legacyYawBasis) { yawOnPlot = onPlot; return; }
    if (!deps.farField.syncPlotFrame()) return;
    yawOnPlot = onPlot;
    const yaw = focusCam.state.yaw;
    focusCam.yawFrame(_yawN, _yawE);
    if (onPlot) {
      _yawD.copy(_yawN).multiplyScalar(Math.cos(yaw)).addScaledVector(_yawE, Math.sin(yaw));
      deps.farField.worldDirToPlot(_yawD);
      if (_yawD.x * _yawD.x + _yawD.z * _yawD.z < 1e-12) return;
      focusCam.setOrientation(Math.atan2(_yawD.x, -_yawD.z), focusCam.state.pitch);
    } else {
      deps.farField.plotDirToWorld(_yawD.set(Math.sin(yaw), 0, -Math.cos(yaw)));
      focusCam.setOrientation(Math.atan2(_yawD.dot(_yawE), _yawD.dot(_yawN)), focusCam.state.pitch);
    }
  }

  /** Hand the plot's local rig the altitude the focus camera is holding. */
  function enterPlotRegime(): void {
    syncYawBasis();
    plotPivot(orbit.lookAt);
    // On the GROUND at the anchor, not at y = 0. Local y = 0 is the
    // DTM's MINIMUM elevation, 150 m under the surface at Jezero's centre, and
    // the eye is `plotEye` measured FROM here: aiming there put the ground
    // inside the near plane at the clamp, so the walkaround drew sky and
    // nothing else with the eye still 2 m above the terrain.
    deps.live.plot?.liftAboveGround(orbit.lookAt, 0);
    orbit.distance = plotDistanceFromAltitude();
    camPos.copy(plotEye());
    camTarget.copy(orbit.lookAt);
    targetPos.copy(camPos);
    targetLook.copy(camTarget);
  }

  /**
   * Stand the eye up on a body with no site to walk around on.
   *
   * The ladder closes toward overhead as the ground becomes the subject
   * (`applyTierFraming`'s 1.05 rad at tier 3 and below), and `pitchCeiling`
   * cuts that to the frame's own oblique - 18.3 degrees against a 22.5 degree
   * half-frame, which puts the horizon at ndcY 0.8. The walkaround's own
   * `PLOT_PITCH_FLOOR` is lower again and is the pose that has the horizon in
   * frame with ground under it, so a descent with no mesh to hand over to
   * takes it rather than the ceiling's.
   * Only on smooth ground with no site row, which is a fact the moment the band
   * is crossed - a plot MESH lands about a second later, on Mars from its site
   * and on a siteless body with relief from its leaves, so asking for one here
   * would restand their entry. A `goTo` into the band is not a descent and
   * stands whatever the ground (`standingEyeFloorM` in src/atlas/focusRig.ts).
   */
  function enterStandingPose(): void {
    if (deps.injects.state.legacyStanding || site() !== undefined || !descendable()) return;
    if (!deps.injects.state.legacySitelessStand && focusCam.steppedIntoSurface
      && !deps.bodyReaders.smoothGround(focusCam.state.bodyId)) return;
    if (focusCam.state.pitch <= PLOT_PITCH_FLOOR) return;
    const restore = focusCam.pitchMemory() ?? focusCam.state.pitch;
    if (STAND_MEMORY_DEBUG) focusCam.setOrientation(focusCam.state.yaw, PLOT_PITCH_FLOOR, restore);
    else {
      standingRestore = restore;
      focusCam.setOrientation(focusCam.state.yaw, PLOT_PITCH_FLOOR);
    }
  }

  /** The pitch the player had before the stand; the wheel holds the stand while in SURFACE and gets this back on the way out. */
  let standingRestore: number | null = null;
  /** `?standmemory=1`: the stand arms the pre-stand pitch as the memory, so the next notch undoes it. `descent-dojo --scroll-descent --inject stand-memory` reaches it. */
  const STAND_MEMORY_DEBUG = TEST_SWITCHES && urlFlag('standmemory');

  /** The ladder's step down across the SURFACE edge, mesh or no mesh. */
  function enterSurfaceBand(): void {
    if (deps.injects.state.plotEdgeOnly) void ensurePlotMesh();
    enterPlotRegime();
    enterStandingPose();
  }
  /** And back up across it. */
  function leaveSurfaceBand(): void {
    // Leaving SURFACE drops the camera's hold on the rover; re-entry lands
    // on the anchor `enterPlotRegime` just aimed at, and only JUMP TO
    // ROVER hands the camera back to it.
    deps.roverDrive.state.followRover = false;
    // Cutting a jump off here rather than letting it finish would otherwise
    // leave the ease's own fraction of `plotTilt` standing.
    if (deps.roverDrive.state.jumpEaseT !== null && !lookUpDebug.skipJumpTiltReset) plotTilt = 0;
    deps.roverDrive.state.jumpEaseT = null;
    if (deps.injects.state.greyFrameInject) deps.injects.state.greyFramePending = true;
    if (standingRestore !== null && focusCam.pitchMemory() !== null) {
      focusCam.setOrientation(focusCam.state.yaw, focusCam.state.pitch, standingRestore);
    }
    standingRestore = null;
  }

  function plotDistanceFromAltitude(): number {
    // The eye floor is a floor on the DISTANCE, because the pivot is on the
    // ground and `plotEye` puts the eye `distance` from it at `pitch`. Lifting
    // the eye instead would keep the aim on the pivot and tilt the whole view
    // down at it; this keeps the eye looking along the ground. It used to be
    // read off the near plane, which is now read off the eye.
    const pitch = Math.max(plotPitchFloor(), focusCam.state.pitch);
    const minEyeM = plotEyeFloorM() / Math.max(0.02, Math.sin(pitch));
    const minD = deps.live.plot ? Math.max(PLOT_MIN_DISTANCE_M, minEyeM) : 0.004 * plotUnitsM();
    return THREE.MathUtils.clamp(altitude(), minD, 9.0 * plotUnitsM());
  }

  /** `--band --inject near-clip`: the near plane back outside the eye's own height. */
  let nearClipInject = false;

  /** `--mover --inject cinema-release`: `stopCinema()`'s pre-fix eye distance. */
  let cinemaReleaseInject = false;

  /**
   * `--band --inject near-clip`'s override on `camera.near`: TWICE the eye's
   * own clearance over the mesh, in metres, so no eye height can outgrow it.
   */
  function applyNearClipInject(): boolean {
    if (!nearClipInject || !deps.live.plot) return false;
    const clearM = Math.max(0.05, deps.live.plot.clearanceAboveGround(camPos));
    setCameraNear(clearM * 2);
    return true;
  }

  function setCameraNear(units: number): void {
    if (Math.abs(camera.near - units) <= units * 1e-3) return;
    camera.near = units;
    camera.updateProjectionMatrix();
  }

  /** The plot rig's eye, in the plot's own frame, from the shared yaw and pitch. */
  const _eye = new THREE.Vector3();
  function plotEye(): THREE.Vector3 {
    const pitch = Math.max(plotPitchFloor(), focusCam.state.pitch);
    const cy = Math.cos(pitch);
    const sy = Math.sin(pitch);
    const cx = Math.cos(focusCam.state.yaw);
    const sx = Math.sin(focusCam.state.yaw);
    const d = orbit.distance;
    // MINUS on z: the plot's local frame is the region's, +z south, and yaw 0
    // here is its north; `syncYawBasis` carries the focus rig's yaw onto it.
    return _eye.set(
      orbit.lookAt.x + d * cy * sx,
      orbit.lookAt.y + d * sy,
      orbit.lookAt.z - d * cy * cx,
    );
  }

  /** Up to the zenith: the untilted view looks down at the pivot by about the pose's own pitch. */
  function plotTiltMax(): number {
    return lookUpDebug.noTilt ? 0 : Math.PI / 2 + Math.max(plotPitchFloor(), focusCam.state.pitch);
  }

  /**
   * The walkaround with no plot mesh: SURFACE over smooth ground, where the eye
   * stands on the tiles at 1.7 m and `ensurePyramidPlot` never builds a plot.
   */
  function tileWalkaround(): boolean {
    return ladderOwnsFrame() && surfaceRegime() && deps.live.plot === null
      && deps.bodyReaders.smoothGround(focusCam.state.bodyId);
  }

  const _walkA = new THREE.Vector3();
  const _walkUp = new THREE.Vector3();
  const _walkFwd = new THREE.Vector3();
  const _walkRight = new THREE.Vector3();
  const _walkQ = new THREE.Quaternion();
  /** Step the tile walkaround's anchor along the eye's heading by `frac` of the eye's range to it. */
  function tileWalk(forward: number, right: number, frac: number): void {
    const s = focusCam.state;
    const body = deps.bodyScene.state.orrery?.byId(s.bodyId);
    const a = s.latDeg === null || s.lonDeg === null ? null
      : deps.focusRig.focusBodies.surface(s.bodyId, s.latDeg, s.lonDeg);
    const len = Math.hypot(forward, right);
    if (!body || !a || len === 0) return;
    const eye = focusCam.eyeWorld();
    _walkUp.set(a.x - body.world.x, a.y - body.world.y, a.z - body.world.z).normalize();
    _walkFwd.set(a.x - eye.x, a.y - eye.y, a.z - eye.z);
    const step = (_walkFwd.length() * frac) / len;
    _walkFwd.addScaledVector(_walkUp, -_walkFwd.dot(_walkUp));
    if (_walkFwd.lengthSq() < 1e-12) return;
    _walkFwd.normalize();
    _walkRight.crossVectors(_walkFwd, _walkUp);
    _walkA.set(a.x - body.world.x, a.y - body.world.y, a.z - body.world.z)
      .addScaledVector(_walkFwd, forward * step).addScaledVector(_walkRight, right * step)
      .applyQuaternion(_walkQ.copy(body.quaternion).invert()).normalize();
    const lat = THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(_walkA.y, -1, 1)));
    const lon = (THREE.MathUtils.radToDeg(Math.atan2(-_walkA.z, _walkA.x)) + 360) % 360;
    // A pinned anchor is re-applied every frame, so the pin walks with it.
    const pin = deps.descentAnchorHandle.anchorPin;
    if (pin && pin.body === s.bodyId) deps.descentAnchorHandle.pinDescentAnchor({ body: s.bodyId, lat, lon });
    else focusCam.setAnchor(lat, lon);
  }

  /** A drag's orbit: on a plot, upward past the pitch floor becomes tilt, and downward spends the tilt first. */
  function orbitViewBy(dYaw: number, dPitch: number): void {
    if (dPitch !== 0) focusCam.pinPitch();
    const onPlot = plotCamera();
    if ((!onPlot && !tileWalkaround()) || cinema !== null || lookUpDebug.noTilt) {
      focusCam.orbitBy(dYaw, dPitch);
      return;
    }
    let pitchPart = dPitch;
    if (dPitch > 0 && plotTilt > 0) {
      const back = Math.min(plotTilt, dPitch);
      plotTilt -= back;
      pitchPart -= back;
    } else if (dPitch < 0 && onPlot) {
      pitchPart = Math.max(dPitch, -Math.max(0, focusCam.state.pitch - plotPitchFloor()));
    }
    const before = focusCam.state.pitch;
    focusCam.orbitBy(dYaw, pitchPart);
    if (dPitch < 0) {
      plotTilt = THREE.MathUtils.clamp(
        plotTilt - dPitch - (before - focusCam.state.pitch), 0, plotTiltMax());
    }
  }

  const _tiltFwd = new THREE.Vector3();
  const _tiltUp = new THREE.Vector3();
  /** Turn the placed view up about the eye, never past the zenith. */
  function applyPlotTilt(): void {
    if (plotTilt <= 0) return;
    let up: THREE.Vector3 = plotUpWorld;
    if (!plotCamera()) {
      const c = deps.focusRig.focusBodies.centre(focusCam.state.bodyId);
      const eye = focusCam.eyeWorld();
      if (!c) return;
      up = _tiltUp.set(eye.x - c.x, eye.y - c.y, eye.z - c.z).normalize();
    }
    const fwd = camera.getWorldDirection(_tiltFwd);
    const elev = Math.asin(THREE.MathUtils.clamp(fwd.dot(up), -1, 1));
    const tilt = Math.min(plotTilt, Math.PI / 2 - elev);
    if (tilt > 0) {
      camera.rotateX(tilt);
      camera.updateMatrixWorld();
    }
  }

  /** The focused body's own system: its primary, its moons, and the primary's other moons. */
  function inLocalSky(row: BodyRow): boolean {
    const own = deps.bodyScene.state.bodyNode?.row;
    if (!own || row.id === own.id) return false;
    if (row.parent === own.id) return true;
    if (own.parent === null || own.parent === 'sol') return false;
    return row.id === own.parent || row.parent === own.parent;
  }

  /** The focus rig's own placement: everything the tick's else-branch did inline. */
  function placeFocusCamera(): void {
    setCameraNear(SCENE_NEAR);
    focusCam.apply();
    if (tileWalkaround()) applyPlotTilt();
    camPos.copy(camera.position);
    camTarget.set(0, 0, 0);
    targetPos.copy(camPos);
    targetLook.copy(camTarget);
  }

  const _camW = new THREE.Vector3();
  function readCameraWorld(): void {
    if (deps.worldHostState.externalWorld) {
      cameraWorld.x = externalCamWorld.x;
      cameraWorld.y = externalCamWorld.y;
      cameraWorld.z = externalCamWorld.z;
      return;
    }
    // Inside the claim the walkaround rig owns the eye, not the focus camera:
    // the focus rig still tracks its lat/lon down there, and publishing ITS eye
    // while the frame is drawn from `camPos` is what put the registered world a
    // few metres off. `camPos` is in the plot's own frame, so it goes out
    // through the same placement `dtmGroup` is given.
    if (plotCamera() && deps.farField.syncPlotFrame()) {
      _camW.copy(camPos).applyQuaternion(plotQuat);
      cameraWorld.x = plotWorld.x + _camW.x;
      cameraWorld.y = plotWorld.y + _camW.y;
      cameraWorld.z = plotWorld.z + _camW.z;
      return;
    }
    const eye = focusCam.eyeWorld();
    cameraWorld.x = eye.x;
    cameraWorld.y = eye.y;
    cameraWorld.z = eye.z;
  }

  function stopCinema(): void {
    if (cinema === null) return;
    cinema = null;
    pcFly.classList.remove('on');
    // The flight forced the sol sweep on; ending the flight ends that sweep
    // too, so no preview survives the click that stopped the flyover.
    if (deps.viewClockHandle.state.playing === 'sol') setPlaying(null);
    // Hand the orbit camera back where the flight left off, so releasing
    // control is not itself a cut.
    // In the plot's own frame, which is where the rig and the flyover's path
    // both are; the drawn camera is at the scene origin inside the claim.
    const d = camPos.clone().sub(orbit.lookAt);
    const len = Math.max(d.length(), 1e-6);
    // `cinemaReleaseInject` restores the pre-fix expression, which multiplies
    // the metre distance by the extent instead of scaling the clamp bounds.
    focusCam.setAltitude(cinemaReleaseInject
      ? THREE.MathUtils.clamp(len, 1.15, 9) * plotUnitsM()
      : THREE.MathUtils.clamp(len, 1.15 * plotUnitsM(), 9 * plotUnitsM()));
    focusCam.setOrientation(
      Math.atan2(d.x, d.z),
      Math.asin(THREE.MathUtils.clamp(d.y / len, -1, 1)),
    );
    deps.live.cameraDirty = true;
    // The flyover can leave altitude/pitch anywhere PITCH_LIMIT and the ladder
    // bands do not agree on; resync the label rather than leave it stale.
    deps.navigation.syncTierToAltitude();
  }

  return {
    WALK_BODY_R_M, _aimW, applyNearClipInject, applyPlotTilt, applyTierFraming, aspect, camPos, camTarget,
    enterPlotRegime, enterSurfaceBand, formatAltitude, formatSample, inLocalSky, ladderOwnsFrame, leaveSurfaceBand,
    orbit, orbitViewBy, plotCamera, plotDistanceFromAltitude, plotEye, plotEyeFloorM, plotPitchFloor, plotPivot,
    plotTiltMax, plotUnitsM, setCameraNear, shipPlace, surfaceRegime, syncYawBasis, targetLook, targetPos, lookUpDebug,
    tileWalk, tileWalkaround,
    cinemaLook, cinemaPos, placeFocusCamera, readCameraWorld, stopCinema,
    get plotTilt() { return plotTilt; },
    set plotTilt(v) { plotTilt = v; },
    get cinema() { return cinema; },
    set cinema(v) { cinema = v; },
    get cinemaReleaseInject() { return cinemaReleaseInject; },
    set cinemaReleaseInject(v) { cinemaReleaseInject = v; },
    get handoverMarsTint() { return handoverMarsTint; },
    set handoverMarsTint(v) { handoverMarsTint = v; },
    get handoverStepM() { return handoverStepM; },
    set handoverStepM(v) { handoverStepM = v; },
    get legacyPlotHandover() { return legacyPlotHandover; },
    set legacyPlotHandover(v) { legacyPlotHandover = v; },
    get legacyYawBasis() { return legacyYawBasis; },
    set legacyYawBasis(v) { legacyYawBasis = v; },
    get nearClipInject() { return nearClipInject; },
    set nearClipInject(v) { nearClipInject = v; },
  };
}
